import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SearchBar } from '../components/SearchBar';
import { BusinessCard } from '../components/BusinessCard';
import { BusinessListView } from '../components/BusinessListView';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { SavedSearchesSidebar } from '../components/SavedSearchesSidebar';
import { BulkUpload } from '../components/BulkUpload';
import { useAuth } from '../contexts/AuthContext';
import { searchBusinesses, enrichBusinessData, optimizeRoute } from '../services/geminiProxyService';
import { exportToCsv } from '../utils/csvExporter';
import {
  fetchSavedSearches,
  saveSearch,
  loadSavedSearch,
  combineSavedSearches,
  deleteSavedSearch
} from '../services/savedSearchesService';
import {
  getGoogleAuthStatus,
  exportToGoogleSheets,
  appendToGoogleSheet
} from '../services/googleSheetsService';
import { GoogleSheetsExportModal, type ExportOptions } from '../components/GoogleSheetsExportModal';
import type { Business, SavedSearch } from '../types';
import { API_BASE_URL } from '../config/api';

export default function Search() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBusinesses, setSelectedBusinesses] = useState<Set<number>>(new Set());
  const [resultLimit, setResultLimit] = useState('20');
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [usage, setUsage] = useState<any>(null);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isHowToUseCollapsed, setIsHowToUseCollapsed] = useState(true);
  const [isSavedSearchesLoading, setIsSavedSearchesLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [columnCount, setColumnCount] = useState<3 | 4 | 5>(3);
  const [hasGoogleAuth, setHasGoogleAuth] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportBusinesses, setExportBusinesses] = useState<Business[]>([]);

  // Get grid column classes based on selected column count
  const getGridClasses = () => {
    const baseClasses = 'grid grid-cols-1 md:grid-cols-2 gap-4';
    const columnClasses = {
      3: 'lg:grid-cols-3',
      4: 'lg:grid-cols-4',
      5: 'lg:grid-cols-5',
    };
    return `${baseClasses} ${columnClasses[columnCount]}`;
  };

  // Fetch usage statistics and saved searches
  useEffect(() => {
    fetchUsage();
    loadSavedSearchesList();
    checkGoogleAuthStatus();
  }, []);

  const checkGoogleAuthStatus = async () => {
    try {
      const status = await getGoogleAuthStatus();
      setHasGoogleAuth(status.hasGoogleAuth && status.isTokenValid);
    } catch (err) {
      console.error('Failed to check Google auth status:', err);
      setHasGoogleAuth(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/usage`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data.usage);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const loadSavedSearchesList = async () => {
    setIsSavedSearchesLoading(true);
    try {
      const searches = await fetchSavedSearches();
      setSavedSearches(searches);
    } catch (err) {
      console.error('Failed to load saved searches:', err);
    } finally {
      setIsSavedSearchesLoading(false);
    }
  };

  const handleSaveSearch = async () => {
    if (results.length === 0) {
      setError('No results to save. Please perform a search first.');
      return;
    }

    const name = prompt('Enter a name for this search:');
    if (!name || !name.trim()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await saveSearch({
        name: name.trim(),
        queryText: query,
        results,
      });
      setSuccessMessage(`Search "${name}" saved successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadSavedSearchesList();
    } catch (err: any) {
      setError(err.message || 'Failed to save search');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadSavedSearch = async (id: number) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const savedSearch = await loadSavedSearch(id);
      setQuery(savedSearch.query);
      setResults(savedSearch.results);
      setSelectedBusinesses(new Set());
      setSuccessMessage(`Loaded "${savedSearch.name}" successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to load saved search');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSavedSearch = async (id: number) => {
    const confirmed = confirm('Are you sure you want to delete this saved search?');
    if (!confirmed) {
      return;
    }

    setIsSavedSearchesLoading(true);
    setError(null);

    try {
      await deleteSavedSearch(id);
      setSuccessMessage('Search deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadSavedSearchesList();
    } catch (err: any) {
      setError(err.message || 'Failed to delete search');
    } finally {
      setIsSavedSearchesLoading(false);
    }
  };

  const handleLoadAndCombine = async (ids: number[]) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const combined = await combineSavedSearches(ids);

      // Load combined results into the view (duplicates are already removed by the API)
      setResults(combined.results);
      setSelectedBusinesses(new Set());
      setQuery(`Combined ${ids.length} searches`);

      setSuccessMessage(`Loaded and combined ${ids.length} searches into ${combined.resultCount} unique businesses!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to load and combine searches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCombineAndExport = async (ids: number[]) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const combined = await combineSavedSearches(ids);

      // Export combined results to CSV
      exportToCsv(combined.results, `combined-search-${Date.now()}.csv`);

      setSuccessMessage(`Combined ${ids.length} searches and exported ${combined.resultCount} results!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to combine and export searches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults([]);
    setSelectedBusinesses(new Set());

    try {
      const data = await searchBusinesses(query, userLocation, resultLimit);
      setResults(data.results);

      // Refresh usage after search
      await fetchUsage();

      if (data.results.length === 0) {
        setError('No businesses found. Try adjusting your search query.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search businesses. Please try again.');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setQuery(prev => `${prev} near my current location`);
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      setError('Geolocation is not supported by your browser.');
    }
  };

  const toggleBusinessSelection = (id: number) => {
    setSelectedBusinesses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedBusinesses(new Set(results.map(b => b.id)));
  };

  const deselectAll = () => {
    setSelectedBusinesses(new Set());
  };

  const handleExport = () => {
    const selected = results.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to export');
      return;
    }

    try {
      exportToCsv(selected, `business-search-${Date.now()}.csv`);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to export businesses');
    }
  };

  const handleExportToSheets = () => {
    if (!hasGoogleAuth) {
      setError('Please connect your Google account first from the Account page');
      return;
    }

    const selected = results.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to export');
      return;
    }

    setExportBusinesses(selected);
    setShowExportModal(true);
  };

  const handleModalExport = async (options: ExportOptions) => {
    setIsLoading(true);
    setShowExportModal(false);
    setError(null);

    try {
      // Filter data to selected columns only
      const filteredData = exportBusinesses.map(business =>
        Object.fromEntries(
          options.selectedColumns.map(col => [col, business[col as keyof Business]])
        )
      );

      if (options.mode === 'create') {
        const result = await exportToGoogleSheets(filteredData, options.title, options.selectedColumns);
        setSuccessMessage(`Exported ${filteredData.length} businesses to Google Sheets!`);
        setTimeout(() => {
          setSuccessMessage(null);
          window.open(result.spreadsheetUrl, '_blank');
        }, 2000);
      } else {
        await appendToGoogleSheet(options.spreadsheetId!, filteredData, options.sheetName, options.selectedColumns);
        setSuccessMessage(`Appended ${filteredData.length} rows to Google Sheets!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      if (err.message === 'GOOGLE_AUTH_REQUIRED') {
        setError('Please connect your Google account first from the Account page');
      } else {
        setError(err.message || 'Failed to export to Google Sheets');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnrich = async () => {
    const selected = results.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to enrich');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const enrichmentOptions = ['website', 'email', 'phone', 'instagram', 'facebook', 'linkedin'];
      const enrichedResults = await Promise.all(
        results.map(async (business) => {
          if (selectedBusinesses.has(business.id)) {
            try {
              const enrichedData = await enrichBusinessData(business, enrichmentOptions);
              return { ...business, ...enrichedData };
            } catch (err) {
              console.error(`Failed to enrich ${business.name}`);
              return business;
            }
          }
          return business;
        })
      );

      setResults(enrichedResults);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to enrich business data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptimizeRoute = async () => {
    const selected = results.filter(b => selectedBusinesses.has(b.id));
    if (selected.length < 2) {
      setError('Please select at least 2 businesses to optimize route');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const addresses = selected.map(b => b.address).filter(Boolean) as string[];
      const optimizedAddresses = await optimizeRoute(addresses, userLocation, null, null);

      // Reorder results based on optimized route
      const addressToIndex = new Map(optimizedAddresses.map((addr, idx) => [addr, idx]));
      const reordered = [...results].sort((a, b) => {
        const aIndex = addressToIndex.get(a.address) ?? 999;
        const bIndex = addressToIndex.get(b.address) ?? 999;
        return aIndex - bIndex;
      });

      setResults(reordered);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to optimize route');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 max-w-7xl mx-auto py-4 lg:py-6 px-4 sm:px-6 lg:px-8 min-h-screen">
        {/* Saved Searches Sidebar - Hidden on mobile when collapsed */}
        <div className={`${isSidebarCollapsed ? 'hidden lg:block' : 'block'}`}>
          <SavedSearchesSidebar
            savedSearches={savedSearches}
            isLoading={isSavedSearchesLoading}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onLoadSearch={handleLoadSavedSearch}
            onDeleteSearch={handleDeleteSavedSearch}
            onCombineAndExport={handleCombineAndExport}
            onLoadAndCombine={handleLoadAndCombine}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-4 lg:space-y-6 min-w-0">
        {/* Header with Usage Stats */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold mb-2">Business Search</h1>
                <p className="text-blue-50 text-sm sm:text-base">
                  AI-powered business discovery across the United States
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                {usage && (
                  <div className="text-left sm:text-right">
                    <div className="text-sm text-blue-50">Today's Usage</div>
                    <div className="text-2xl font-bold">{usage.today.results} / {usage.today.limit || '∞'}</div>
                    <div className="text-xs text-blue-50 mt-1 capitalize">{usage.subscriptionTier} Plan</div>
                  </div>
                )}
                <button
                  onClick={() => setIsHowToUseCollapsed(!isHowToUseCollapsed)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <svg className={`w-4 h-4 transition-transform duration-200 ${isHowToUseCollapsed ? '' : 'rotate-180'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">How It Works</span>
                  <span className="sm:hidden">Help</span>
                </button>
              </div>
            </div>
          </div>

          {/* Collapsible How It Works Section */}
          {!isHowToUseCollapsed && (
            <div className="bg-white/10 border-t border-white/20 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                {/* Search Tips */}
                <div>
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Search Tips
                  </h3>
                  <ul className="text-blue-50 text-xs space-y-1">
                    <li>• Be specific with location</li>
                    <li>• Use business type keywords</li>
                    <li>• Try "near [city, state]"</li>
                  </ul>
                </div>

                {/* Export Data */}
                <div>
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Export CSV
                  </h3>
                  <ul className="text-blue-50 text-xs space-y-1">
                    <li>• Select businesses</li>
                    <li>• Click "Export CSV"</li>
                    <li>• Import to your CRM</li>
                  </ul>
                </div>

                {/* Save Searches */}
                <div>
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                    </svg>
                    Save Searches
                  </h3>
                  <ul className="text-blue-50 text-xs space-y-1">
                    <li>• Save for later use</li>
                    <li>• Combine multiple searches</li>
                    <li>• Export combined lists</li>
                  </ul>
                </div>

                {/* View Options */}
                <div>
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    View Options
                  </h3>
                  <ul className="text-blue-50 text-xs space-y-1">
                    <li>• Switch card/list view</li>
                    <li>• Adjust column count</li>
                    <li>• Select/deselect all</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6">
          <SearchBar
            query={query}
            setQuery={setQuery}
            onSearch={handleSearch}
            onUseLocation={handleUseLocation}
            isLoading={isLoading}
            resultLimit={resultLimit}
            setResultLimit={setResultLimit}
          />

          {userLocation && (
            <div className="mt-3 text-sm text-cyan-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Location enabled
            </div>
          )}
        </div>

        {/* Bulk Upload (Pro/Enterprise only) */}
        {user && (user.subscriptionTier === 'pro' || user.subscriptionTier === 'enterprise') && (
          <BulkUpload />
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-900/50 border border-green-600 rounded-lg p-4 text-green-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-red-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingSpinner message="Searching for businesses..." />}

        {/* Results Section */}
        {!isLoading && results.length > 0 && (
          <>
            {/* Action Buttons */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-3 lg:p-4">
              <div className="flex flex-col gap-3">
                {/* Selection and View Controls */}
                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
                  <span className="px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium text-sm text-center sm:text-left whitespace-nowrap">
                    {selectedBusinesses.size} of {results.length} selected
                  </span>
                  <div className="flex gap-2 flex-1 sm:flex-initial">
                    <button
                      onClick={selectAll}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all text-sm"
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAll}
                      className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Deselect All
                    </button>
                  </div>

                  {/* View Toggle */}
                  <div className="flex items-center justify-center gap-1 bg-slate-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`px-3 py-1 rounded-md transition-colors ${
                        viewMode === 'card'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          : 'text-slate-300 hover:text-white'
                      }`}
                      title="Card View"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1 rounded-md transition-colors ${
                        viewMode === 'list'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          : 'text-slate-300 hover:text-white'
                      }`}
                      title="List View"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {/* Column Count Selector (only show in card view on larger screens) */}
                  {viewMode === 'card' && (
                    <div className="hidden md:flex items-center gap-2">
                      <span className="text-sm text-slate-400">Columns:</span>
                      <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                        <button
                          onClick={() => setColumnCount(3)}
                          className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                            columnCount === 3
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : 'text-slate-300 hover:text-white'
                          }`}
                          title="3 Columns"
                        >
                          3
                        </button>
                        <button
                          onClick={() => setColumnCount(4)}
                          className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                            columnCount === 4
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : 'text-slate-300 hover:text-white'
                          }`}
                          title="4 Columns"
                        >
                          4
                        </button>
                        <button
                          onClick={() => setColumnCount(5)}
                          className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                            columnCount === 5
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : 'text-slate-300 hover:text-white'
                          }`}
                          title="5 Columns"
                        >
                          5
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  <button
                    onClick={handleSaveSearch}
                    disabled={results.length === 0}
                    className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                    </svg>
                    <span className="hidden sm:inline">Save Search</span>
                    <span className="sm:hidden">Save</span>
                  </button>

                  <button
                    onClick={handleExport}
                    disabled={selectedBusinesses.size === 0}
                    className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden sm:inline">Export CSV</span>
                    <span className="sm:hidden">CSV</span>
                  </button>

                  {hasGoogleAuth && (
                    <button
                      onClick={handleExportToSheets}
                      disabled={selectedBusinesses.size === 0}
                      className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                        <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                      </svg>
                      <span className="hidden lg:inline">Export to Sheets</span>
                      <span className="lg:hidden">Sheets</span>
                    </button>
                  )}

                  <button
                    onClick={handleEnrich}
                    disabled={selectedBusinesses.size === 0}
                    className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 7H7v6h6V7z" />
                      <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden sm:inline">Enrich Data</span>
                    <span className="sm:hidden">Enrich</span>
                  </button>

                  <button
                    onClick={handleOptimizeRoute}
                    disabled={selectedBusinesses.size < 2}
                    className="px-3 sm:px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm col-span-2 sm:col-span-1"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden sm:inline">Optimize Route</span>
                    <span className="sm:hidden">Optimize</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Results Display */}
            {viewMode === 'card' ? (
              <div className={getGridClasses()}>
                {results.map((business) => (
                  <BusinessCard
                    key={business.id}
                    business={business}
                    isSelected={selectedBusinesses.has(business.id)}
                    onSelectionChange={toggleBusinessSelection}
                  />
                ))}
              </div>
            ) : (
              <BusinessListView
                businesses={results}
                selectedBusinesses={selectedBusinesses}
                onSelectionChange={toggleBusinessSelection}
              />
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && results.length === 0 && !error && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-slate-300 mb-2">Start Searching</h3>
            <p className="text-slate-500">
              Enter a query above to discover businesses across the United States
            </p>
          </div>
        )}
        </div>
      </div>

      <GoogleSheetsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleModalExport}
        businesses={exportBusinesses}
      />
    </Layout>
  );
}
